import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Link2, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  Settings,
  RefreshCw,
  ExternalLink,
  Key,
  Building,
  Percent,
  Zap
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const AxonautIntegrationPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [config, setConfig] = useState({
    enabled: false,
    auto_sync: false,
    api_key: '',
    company_id: '',
    default_tax_rate: 20.0,
    api_key_configured: false
  });
  
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchSyncStatus();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/settings/integrations/axonaut');
      setConfig(prev => ({
        ...prev,
        ...res.data,
        api_key: '' // Don't show actual key
      }));
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const res = await api.get('/invoices/axonaut/sync-status');
      setSyncStatus(res.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/integrations/axonaut', {
        enabled: config.enabled,
        auto_sync: config.auto_sync,
        api_key: config.api_key || undefined,
        company_id: config.company_id,
        default_tax_rate: config.default_tax_rate
      });
      toast.success('Configuration sauvegardée');
      fetchConfig();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await api.post('/settings/integrations/axonaut/test');
      if (res.data.success) {
        toast.success(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d]">
                Intégration Axonaut
              </h1>
              <p className="text-[#64748b]">
                Synchronisez vos factures avec votre compte Axonaut
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Card */}
          <Card className="card lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-blue-500" />
                Configuration
              </CardTitle>
              <CardDescription>
                Configurez votre connexion à Axonaut
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Integration */}
              <div className="flex items-center justify-between p-4 bg-[#f8fafc] rounded-xl">
                <div>
                  <Label className="text-base font-medium">Activer l&apos;intégration</Label>
                  <p className="text-sm text-[#64748b]">
                    Permet la synchronisation des factures vers Axonaut
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
                />
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Clé API Axonaut
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={config.api_key}
                    onChange={(e) => setConfig({...config, api_key: e.target.value})}
                    placeholder={config.api_key_configured ? "••••••••••••" : "Entrez votre clé API"}
                    className="flex-1"
                  />
                  {config.api_key_configured && (
                    <Badge className="bg-emerald-100 text-emerald-700 self-center">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Configurée
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-[#94a3b8]">
                  Trouvez votre clé API dans Axonaut : Paramètres &gt; Intégrations &gt; API
                </p>
              </div>

              {/* Company ID */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  ID Entreprise (optionnel)
                </Label>
                <Input
                  value={config.company_id || ''}
                  onChange={(e) => setConfig({...config, company_id: e.target.value})}
                  placeholder="Votre ID entreprise Axonaut"
                />
              </div>

              {/* Default Tax Rate */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Taux de TVA par défaut
                </Label>
                <Input
                  type="number"
                  value={config.default_tax_rate}
                  onChange={(e) => setConfig({...config, default_tax_rate: parseFloat(e.target.value) || 20})}
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>

              {/* Auto Sync */}
              <div className="flex items-center justify-between p-4 bg-[#f8fafc] rounded-xl">
                <div>
                  <Label className="text-base font-medium">Synchronisation automatique</Label>
                  <p className="text-sm text-[#64748b]">
                    Envoyer automatiquement les factures validées vers Axonaut
                  </p>
                </div>
                <Switch
                  checked={config.auto_sync}
                  onCheckedChange={(checked) => setConfig({...config, auto_sync: checked})}
                  disabled={!config.enabled}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Sauvegarder
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testing || !config.api_key_configured}
                >
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  Tester la connexion
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Status Card */}
          <div className="space-y-6">
            {/* Connection Status */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-base">Statut de connexion</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  {config.enabled && config.api_key_configured ? (
                    <>
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-emerald-700">Connecté</p>
                        <p className="text-sm text-[#64748b]">Mode simulation actif</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <XCircle className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">Non connecté</p>
                        <p className="text-sm text-[#64748b]">Configurez votre clé API</p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sync Stats */}
            {syncStatus && (
              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    Synchronisation
                    <Button variant="ghost" size="sm" onClick={fetchSyncStatus}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#64748b]">Total factures</span>
                    <span className="font-mono font-medium">{syncStatus.total}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#64748b]">Synchronisées</span>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {syncStatus.synced}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#64748b]">En attente</span>
                    <Badge className="bg-amber-100 text-amber-700">
                      {syncStatus.not_synced}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Help Card */}
            <Card className="card bg-blue-50 border-blue-100">
              <CardContent className="p-4">
                <h4 className="font-medium text-blue-800 mb-2">Besoin d&apos;aide ?</h4>
                <p className="text-sm text-blue-600 mb-3">
                  Consultez la documentation Axonaut pour obtenir votre clé API et configurer l&apos;intégration.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-blue-700 border-blue-300"
                  onClick={() => window.open('https://axonaut.com/api', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Documentation API
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AxonautIntegrationPage;
